import { messages } from '../../utils/messages'
import { constants } from '../../utils/constants'
import { generateRandomID, safelyParse } from '../../utils/utils'

export interface ProrithmWebSocketMessage {
  messageType: string
  messageContent: {
    message: string
    macId: string
    category: string
    deviceId?: string
  }
}

export interface ProrithmWebSocketConfig {
  onMessage: (message: ProrithmWebSocketMessage) => void
  onOpen?: () => void
  onClose?: () => void
  onError?: (error: Event) => void
}

class ProrithmWebSocketService {
  private socket: WebSocket | null = null
  private pingInterval: NodeJS.Timeout | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null
  private isConnecting = false
  private config: ProrithmWebSocketConfig | null = null

  connect(config: ProrithmWebSocketConfig): void {
    if (this?.socket?.readyState === WebSocket.OPEN || this?.isConnecting) {
      console.warn(messages?.socketAlreadyConnected)
      return
    }

    this.config = config
    this.isConnecting = true

    try {
      this.socket = new WebSocket(
        `${process.env.REACT_APP_PRORITHM_WEBSOCKET_URL}${generateRandomID(6)}`
      )

      this.socket.onopen = () => {
        console.log(messages?.socketConnected)
        this.isConnecting = false
        this.startPingInterval()
        config?.onOpen?.()
      }

      this.socket.onmessage = (event) => {
        try {
          const message: ProrithmWebSocketMessage = safelyParse(event.data)
          config?.onMessage(message)
        } catch (error) {
          console.error(error)
        }
      }

      this.socket.onclose = (event) => {
        console.log(messages?.socketClosed, event.code, event.reason)
        this.isConnecting = false
        this.stopPingInterval()
        config?.onClose?.()

        if (event.code !== 1000 && event.code !== 1001) {
          this.scheduleReconnect()
        }
      }

      this.socket.onerror = (error) => {
        console.error(messages?.socketError, error)
        this.isConnecting = false
        config?.onError?.(error)
      }
    } catch (error) {
      console.error(messages?.socketCreationError, error)
      this.isConnecting = false
    }
  }

  private startPingInterval(): void {
    this?.stopPingInterval()

    this.pingInterval = setInterval(() => {
      if (this?.socket?.readyState === WebSocket.OPEN) {
        const pingMessage = {
          messageType: constants?.heartbeat,
          messageContent: {
            message: '',
            macId: '',
            category: '',
          },
        }

        this?.socket?.send(JSON.stringify(pingMessage))
      }
    }, Number(process.env.REACT_APP_SOCKET_MESSAGE_INTERVAL))
  }

  private stopPingInterval(): void {
    if (this?.pingInterval) {
      clearInterval(this?.pingInterval)
      this.pingInterval = null
    }
  }

  private scheduleReconnect(): void {
    if (this?.reconnectTimeout) {
      clearTimeout(this?.reconnectTimeout)
    }

    this.reconnectTimeout = setTimeout(() => {
      console.log(messages?.socketConnectionReattempt)
      if (this?.config) {
        this?.connect(this?.config)
      }
    }, 1000)
  }

  disconnect(): void {
    this.isConnecting = false

    if (this?.reconnectTimeout) {
      clearTimeout(this?.reconnectTimeout)
      this.reconnectTimeout = null
    }

    this?.stopPingInterval()

    if (this?.socket) {
      this?.socket?.close(1000, messages?.disconnectedByUser)
      this.socket = null
    }
  }

  isConnected(): boolean {
    return this?.socket?.readyState === WebSocket.OPEN
  }
}

// Create a singleton instance
export const prorithmWebSocketService = new ProrithmWebSocketService()
